import styled from 'styled-components';

interface FileWithPreview extends File {
  preview: string;
}

interface FilePreviewGridProps {
  files: FileWithPreview[];
  onRemove: (index: number) => void;
}

const Grid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.mobile.sm};
    margin-top: ${({ theme }) => theme.spacing.mobile.md};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: ${({ theme }) => theme.spacing.sm};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: ${({ theme }) => theme.spacing.gallery.sm};
    margin-top: ${({ theme }) => theme.spacing.gallery.sm};
  }
`;

const FilePreview = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  position: relative;
  transition: all ${({ theme }) => theme.transitions.gallery.hover};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    background: ${({ theme }) => theme.colors.mobile.surface};
    border: 1px solid ${({ theme }) => theme.colors.mobile.border};
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    padding: ${({ theme }) => theme.spacing.mobile.sm};
    box-shadow: ${({ theme }) => theme.shadows.gallery.subtle};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};

    &:hover {
      transform: translateY(-2px);
      box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
      border-color: ${({ theme }) => theme.colors.primary}30;
    }
  }
`;

const PreviewImage = styled.img`
  width: 100%;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  transition: all ${({ theme }) => theme.transitions.gallery.hover};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 120px;
    border-radius: ${({ theme }) => theme.borderRadius.gallery.subtle};
    margin-bottom: ${({ theme }) => theme.spacing.mobile.xs};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 130px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 150px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    height: 160px;
    border-radius: ${({ theme }) => theme.borderRadius.gallery.subtle};

    &:hover {
      transform: scale(1.02);
    }
  }
`;

const FileName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  word-break: break-word;
`;

const FileSize = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray.dark};
`;

const RemoveButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.xs};
  right: ${({ theme }) => theme.spacing.xs};
  background: ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  transition: all ${({ theme }) => theme.transitions.gallery.quick};
  box-shadow: ${({ theme }) => theme.shadows.gallery.subtle};
  z-index: 10;

  &:hover {
    background-color: ${({ theme }) => theme.colors.error}dd;
    transform: scale(1.1);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 32px;
    height: 32px;
    top: ${({ theme }) => theme.spacing.mobile.xs};
    right: ${({ theme }) => theme.spacing.mobile.xs};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};

    &:active {
      transform: scale(0.95);
      background-color: ${({ theme }) => theme.colors.error}cc;
    }
  }
`;

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function FilePreviewGrid({ files, onRemove }: FilePreviewGridProps) {
  if (files.length === 0) return null;

  return (
    <Grid>
      {files.map((file, index) => (
        <FilePreview key={index}>
          <RemoveButton
            onClick={() => onRemove(index)}
            aria-label={`Remove ${file.name}`}
          >
            ×
          </RemoveButton>
          <PreviewImage src={file.preview} alt={file.name} />
          <FileName>{file.name}</FileName>
          <FileSize>{formatFileSize(file.size)}</FileSize>
        </FilePreview>
      ))}
    </Grid>
  );
}

export type { FilePreviewGridProps };
